import { useEffect, useRef } from "react";
import { Link } from "react-router";
import {
  type AssistantAction,
  AssistantAction_Kind,
  type AssistantBlock,
  type AssistantDraft,
  AssistantDraft_State,
  type AssistantSimulation,
  AssistantSimulation_State,
} from "@/generated/backend/v1/assistant";
import { PlanJob_State } from "@/generated/backend/v1/plan";
import { Struct } from "@/generated/google/protobuf/struct";
import { useFamily, useScenario } from "@/api/queries";
import { errorText } from "@/components/ErrorPanel";
import type { SimulationSummary } from "@/plan-logic/assistant/simulation";
import { formatMonth, fromIso } from "@/plan-logic/lib/dates";
import { money } from "@/plan-logic/lib/format";
import { SUPPORT_EMAIL } from "@/site/links";
import { useApplyDraft, useDiscardDraft, useSimulationFinished } from "./api";
import { CardView } from "./cards";
import { Markdown } from "./markdown";

const month = (iso?: string) => (iso ? formatMonth(fromIso(iso)) : "");

/** The model's words: plain and grey, marked as AI (assistant.css `.ac-ai`). */
function AiText({ text, onNavigate }: { text: string; onNavigate?: () => void }) {
  return (
    <div className="ac-ai" data-testid="assistant-ai">
      <Markdown text={text} onNavigate={onNavigate} />
    </div>
  );
}

// ------------------------------------------------------------------ what-ifs

const VERDICT_CLASS: Record<string, string> = { yes: "good", trade: "warn", no: "bad" };

function Chips({ s }: { s: SimulationSummary }) {
  const chips: [string, string][] = [];
  if (s.shortfall)
    chips.push(["Runs short", `${month(s.shortfall.month)}, ${money(s.shortfall.amountShort)}`]);
  else chips.push(["Cash", "Doesn't run short"]);
  if (s.retirement.monthsLater)
    chips.push([
      "Retirement",
      s.retirement.monthsLater > 0
        ? `${s.retirement.monthsLater} months later`
        : `${-s.retirement.monthsLater} months sooner`,
    ]);
  if (s.months)
    chips.push([
      "Left after the bills",
      `${money(s.leftOverMonthly.withThis)} a month (today ${money(s.leftOverMonthly.today)})`,
    ]);
  if (s.givesWay) chips.push(["Gives way", s.givesWay]);
  return (
    <dl className="ac-chips">
      {chips.map(([k, v]) => (
        <div key={k}>
          <dt>{k}</dt>
          <dd>{v}</dd>
        </div>
      ))}
    </dl>
  );
}

function Simulation({
  sim,
  conversation,
  onNavigate,
}: {
  sim: AssistantSimulation;
  conversation: string;
  onNavigate?: () => void;
}) {
  const running = sim.state === AssistantSimulation_State.RUNNING;
  const scenario = useScenario(running ? sim.scenarioUuid : undefined);
  const finished = useSimulationFinished();
  const reported = useRef(false);
  const jobState = scenario.data?.job?.state;
  const done =
    jobState === PlanJob_State.SUCCEEDED ||
    jobState === PlanJob_State.FAILED ||
    (scenario.isError && (scenario.error as { status?: number })?.status === 404);
  useEffect(() => {
    if (!running || !done || reported.current) return;
    reported.current = true;
    finished.mutate({ conversation, scenario: sim.scenarioUuid });
  }, [running, done, conversation, sim.scenarioUuid, finished]);

  const summary =
    sim.state === AssistantSimulation_State.DONE && sim.summary
      ? (Struct.toJson(sim.summary) as unknown as SimulationSummary)
      : undefined;
  return (
    <section className="ac-card calc sim" data-testid="assistant-simulation">
      <header>
        <span className="ac-badge">What-if, run by Abi&rsquo;s planning engine</span>
        <h4>{sim.title}</h4>
      </header>
      <div className="ac-body">
        <p className="ac-label">What was simulated</p>
        <ul className="ac-simulated">
          {sim.simulated.map((line, i) => (
            <li key={i}>{line}</li>
          ))}
        </ul>
        {running && (
          <p className="ac-running" role="status">
            <span className="ac-spin" aria-hidden="true" />
            {jobState === PlanJob_State.QUEUED
              ? "Waiting for the engine…"
              : "Running your plan with this…"}
          </p>
        )}
        {summary && (
          <>
            <p
              className={`ac-verdict ${VERDICT_CLASS[summary.verdict] ?? ""}`}
              data-testid="assistant-verdict"
            >
              <b>{summary.head}</b> {summary.rest}
            </p>
            <Chips s={summary} />
          </>
        )}
        {sim.state === AssistantSimulation_State.FAILED && (
          <p className="ac-error">{sim.errorMessage || "The engine couldn't run this what-if."}</p>
        )}
      </div>
      <footer>
        {sim.href && (
          <Link to={sim.href} onClick={onNavigate} data-testid="assistant-open-decision">
            {summary
              ? "See it in full, or change it, in the Decision Hub"
              : "Open in the Decision Hub"}
          </Link>
        )}
      </footer>
    </section>
  );
}

// ------------------------------------------------------------------ actions

function mailto(message: string) {
  const q = new URLSearchParams({ subject: "Help with Abi", body: message });
  return `mailto:${SUPPORT_EMAIL}?${q.toString().replace(/\+/g, "%20")}`;
}

function Action({
  action,
  onAct,
}: {
  action: AssistantAction;
  onAct: (a: AssistantAction) => void;
}) {
  if (action.kind === AssistantAction_Kind.CONTACT_SUPPORT) {
    return (
      <div className="ac-action" data-testid="assistant-action">
        <blockquote>{action.message}</blockquote>
        <a className="btn" href={mailto(action.message)}>
          {action.label || "Write to support"}
        </a>
      </div>
    );
  }
  return (
    <div className="ac-action" data-testid="assistant-action">
      <button type="button" className="btn g" onClick={() => onAct(action)}>
        {action.label} &rarr;
      </button>
    </div>
  );
}

// ------------------------------------------------------------------ drafts

function Draft({ draft }: { draft: AssistantDraft }) {
  const family = useFamily();
  const apply = useApplyDraft();
  const discard = useDiscardDraft();
  const pending = draft.state === AssistantDraft_State.PENDING;
  const busy = apply.isPending || discard.isPending;
  return (
    <section className="ac-card draft" data-testid="assistant-draft">
      <header>
        <span className="ac-badge">A change to your data, for you to check</span>
        <h4>
          {pending
            ? "Nothing is saved until you press Apply"
            : draft.state === AssistantDraft_State.APPLIED
              ? "Saved"
              : draft.state === AssistantDraft_State.STALE
                ? "Not saved: your data changed since this was drafted"
                : "Discarded"}
        </h4>
      </header>
      <div className="ac-body">
        <table className="ac-diff">
          <tbody>
            {draft.changes.map((c, i) => (
              <tr key={i}>
                <th scope="row">{c.label}</th>
                <td className="before">{c.before || "—"}</td>
                <td aria-hidden="true">&rarr;</td>
                <td className="after">{c.after || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {apply.isError && <p className="ac-error">{errorText(apply.error)}</p>}
        {draft.state === AssistantDraft_State.APPLIED && (
          <p className="ac-note">Abi is rebuilding your plan with it.</p>
        )}
        {draft.state === AssistantDraft_State.STALE && (
          <p className="ac-note">Ask Abi to draft it again, or change it in Update Data.</p>
        )}
      </div>
      {pending && (
        <footer className="ac-buttons">
          <button
            type="button"
            className="btn"
            disabled={busy || !family.data}
            onClick={() =>
              apply.mutate({ draft: draft.draftUuid, expectedVersion: family.data?.version ?? 0 })
            }
            data-testid="assistant-apply"
          >
            Apply
          </button>
          <button
            type="button"
            className="btn g"
            disabled={busy}
            onClick={() => discard.mutate(draft.draftUuid)}
            data-testid="assistant-discard"
          >
            Discard
          </button>
        </footer>
      )}
    </section>
  );
}

// ------------------------------------------------------------------ any block

export function BlockView({
  block,
  user,
  conversation,
  onAct,
  onNavigate,
}: {
  block: AssistantBlock;
  /** A block of the user's own turn (their words). */
  user: boolean;
  conversation: string;
  onAct: (a: AssistantAction) => void;
  onNavigate?: () => void;
}) {
  const b = block.block;
  switch (b.oneofKind) {
    case "text":
      return user ? (
        <div className="ac-user" data-testid="assistant-user">
          {b.text.text}
        </div>
      ) : (
        <AiText text={b.text.text} onNavigate={onNavigate} />
      );
    case "card":
      return <CardView card={b.card} />;
    case "simulation":
      return <Simulation sim={b.simulation} conversation={conversation} onNavigate={onNavigate} />;
    case "action":
      return <Action action={b.action} onAct={onAct} />;
    case "draft":
      return <Draft draft={b.draft} />;
    case "notice":
      return (
        <p className="ac-notice" role="note" data-testid="assistant-notice">
          {b.notice.text}
        </p>
      );
    default:
      return null;
  }
}
